import { AsyncPipe, CurrencyPipe, DatePipe } from '@angular/common';
import { Component, HostListener, OnInit, computed, effect, inject, signal, viewChild } from '@angular/core';
import { FormControl, FormGroup, NonNullableFormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { NgbActiveModal, NgbModal, NgbModalOptions, NgbNavModule } from '@ng-bootstrap/ng-bootstrap';
import { catchError, combineLatest, defer, EMPTY, from, map, of, switchMap, tap } from 'rxjs';
import { LoaderComponent } from '@app/shared/components/loader/loader.component';
import { VehicleCategoryService } from '@app/core/services/vehicle-category.service';
import { LocationService } from '@app/core/services/location.service';
import { ToastsService } from '@app/shared/components/toasts/toasts.service';
import { getErrorMessages } from '@app/shared/utilities/errorMessages.util';
import {
  RaVersion,
  RentalAgreementCreateModel,
  RentalAgreementDetailsModel,
  RentalAgreementDriverUpdatePayload,
  RentalAgreementStatus,
  RentalAgreementStatusesDisplayNames,
  RentalAgreementUpdateModel
} from '../rental-agreements.model';
import { RentalAgreementDriverDetailsModel } from '../rental-agreement-drivers/rental-agreement-drivers.model';
import { RentalAgreementValueDetailsModel } from '../rental-agreement-values/rental-agreement-values.model';
import { RentalAgreementsService } from '../rental-agreements.service';
import { RentalAgreementDocumentsComponent } from '../rental-agreement-documents/rental-agreement-documents.component';
import { RentalAgreementDriversComponent } from '../rental-agreement-drivers/rental-agreement-drivers.component';
import { RentalAgreementValuesComponent } from '../rental-agreement-values/rental-agreement-values.component';
import { BrokerService } from '@app/core/services/broker.service';
import { CopyToClipboardDirective } from '@app/shared/directives/copy-to-clipboard.directive';
import { DriverCardComponent } from '@app/shared/components/driver-card/driver-card.component';
import { DateAgoPipe } from '@app/pipes/date-ago.pipe';
import { BrokerAutocompleteDirective } from '@app/shared/directives/broker-autocomplete.directive';
import { CurrencyService } from '@app/core/services/currency.service';
import { ReservationsService } from '../../reservations/reservations.service';
import { RentalAgreementDriversUpsertDialogComponent } from '../rental-agreement-drivers/rental-agreement-drivers-upsert-dialog/rental-agreement-drivers-upsert-dialog.component';
import { HttpErrorResponse } from '@angular/common/http';
import { RentalAgreementCloseDialogComponent } from '../rental-agreement-close-dialog/rental-agreement-close-dialog.component';
import { SplitterModule } from 'primeng/splitter';
import { RentalAgreementHistoryComponent } from '../rental-agreement-history/rental-agreement-history.component';
import { DatePickerModule } from 'primeng/datepicker';
import { IftaLabelModule } from 'primeng/iftalabel';
import { SkeletonModule } from 'primeng/skeleton';
import { Router } from '@angular/router';
import { RaStatusBadgeComponent } from '@app/shared/components/ra-status-badge/ra-status-badge.component';
import { PopoverModule } from 'primeng/popover';
import { ButtonModule } from 'primeng/button';
import { MenuModule } from 'primeng/menu';
import { AutoCompleteModule } from 'primeng/autocomplete';
import { LocationAutocompleteDirective } from '@app/shared/directives/location-autocomplete.directive';
import { ReservationStatus, ReservationStatusesDisplayNames } from '../../reservations/reservations.model';
import { ConfirmDialogComponent } from '@app/shared/components/confirm-dialog/confirm-dialog.component';
import { SelectModule } from 'primeng/select';
import { VehicleCategorySelectComponent } from '@app/shared/components/vehicle-category-select/vehicle-category-select.component';
import { EMPTY_GUID, newClientUid, toBackendUid } from '@app/shared/utilities/client-uid.util';
import { AccordionModule } from 'primeng/accordion';

interface RentalAgreementGeneralForm {
  reservationNumber: FormControl<string>;
  status: FormControl<number>;
  dateOut: FormControl<Date | null>;
  dateIn: FormControl<Date | null>;
  pickupLocationUid: FormControl<{ uid: string; selectedLabel: string } | null>;
  dropOffLocationUid: FormControl<{ uid: string; selectedLabel: string } | null>;
  vehicleCategoryUid: FormControl<string>;
  servedVehicleCategoryUid: FormControl<string>;
  brokerClientId: FormControl<{ code: number; selectedLabel: string } | null>;
  brokerReference: FormControl<string>;
  rate: FormControl<string>;
  ratePeriodId: FormControl<number | null>;
  observations: FormControl<string>;
  numberOfDays: FormControl<number | null>;
}

interface RentalAgreementPaymentForm {
  numberOfDays: FormControl<number | null>;
  currency: FormControl<string>;
  taxRate: FormControl<number | null>;
  totalTaxValue: FormControl<number | null>;
  cdwExcess: FormControl<number>;
  tpExcess: FormControl<number>;
  areKmsIncluded: FormControl<boolean>;
  areKmsFreePerDay: FormControl<boolean>;
  freeKmsPerDay: FormControl<number>;
  additionalKmsPrice: FormControl<number>;
  discountAmount: FormControl<number>;
}

interface RentalAgreementForm {
  general: FormGroup<RentalAgreementGeneralForm>;
  payment: FormGroup<RentalAgreementPaymentForm>;
  numberOfDays: FormControl<number | null>;
}

type RentalAgreementsUpsertDialogConfig =
  | { mode: 'create'; reservationNumber?: string }
  | { mode: 'update'; uid: string }
  | { mode: 'readonly'; uid: string; version?: RaVersion };

@Component({
  selector: 'app-rental-agreements-upsert-dialog',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    AsyncPipe,
    NgbNavModule,
    LoaderComponent,
    DatePipe,
    RentalAgreementDocumentsComponent,
    RentalAgreementDriversComponent,
    RentalAgreementValuesComponent,
    CopyToClipboardDirective,
    DriverCardComponent,
    DateAgoPipe,
    CurrencyPipe,
    SplitterModule,
    RentalAgreementHistoryComponent,
    DatePickerModule,
    IftaLabelModule,
    BrokerAutocompleteDirective,
    SkeletonModule,
    RaStatusBadgeComponent,
    PopoverModule,
    ButtonModule,
    MenuModule,
    AutoCompleteModule,
    LocationAutocompleteDirective,
    SelectModule,
    VehicleCategorySelectComponent,
    AccordionModule
  ],
  templateUrl: './rental-agreements-upsert-dialog.component.html',
  host: { class: 'd-flex flex-fill flex-column' }
})
export class RentalAgreementsUpsertDialogComponent implements OnInit {
  private _modal = inject(NgbActiveModal);
  private _modalService = inject(NgbModal);
  private _formBuilder = inject(NonNullableFormBuilder);
  private _vehicleCategoryService = inject(VehicleCategoryService);
  private _locationService = inject(LocationService);
  private _toastsService = inject(ToastsService);
  private _raService = inject(RentalAgreementsService);
  private _brokerService = inject(BrokerService);
  private _currencyService = inject(CurrencyService);
  private _reservationService = inject(ReservationsService);
  private _router = inject(Router);

  // Update mode: whether the persisted drivers/value items have been staged into the drafts,
  // so the update payload can include them. Child edits stay local until Save.
  private _raDriversLoaded = false;
  private _raValuesLoaded = false;
  // Whether the user actually touched the staged children. Untouched collections are omitted
  // from the update payload so a scalar-only save keeps its old semantics (e.g. it is not
  // subject to the item-editing restrictions on closed rental agreements).
  private _raDriversModified = false;
  private _raValuesModified = false;

  driversComponent = viewChild(RentalAgreementDriversComponent);
  valuesComponent = viewChild(RentalAgreementValuesComponent);

  config = signal<RentalAgreementsUpsertDialogConfig>({ mode: 'create' });
  mode = computed(() => this.config().mode);

  uid = computed(() => {
    const config = this.config();
    return config.mode === 'create' ? EMPTY_GUID : config.uid;
  });

  raVersion = computed(() => {
    const config = this.config();
    return config.mode === 'readonly' && config.version ? config.version : 'latest';
  });

  readonly drivers = signal<RentalAgreementDriverDetailsModel[]>([]);
  readonly valueItems = signal<RentalAgreementValueDetailsModel[]>([]);
  readonly mainDriver = computed(() => this.drivers().find((d) => !d.isAdditionalDriver) ?? null);

  form: FormGroup<RentalAgreementForm>;
  loading = false;
  errors: string[] | null = null;
  item: RentalAgreementDetailsModel | null = null;
  hasUnsavedData = false;
  historyCollapsed = true;

  lockMenu = computed(() =>
    this.config().mode === 'readonly'
      ? [{ label: 'Unlock', icon: 'fa-solid fa-unlock fa-fw', command: () => this.unlockEditing() }]
      : [{ label: 'Lock', icon: 'fa-solid fa-lock fa-fw', command: () => this.lockEditing() }]
  );

  private _selectedRentalAgreement$ = of(true).pipe(
    switchMap(() => {
      if (this.config().mode === 'create') return of(null);
      return defer(() => this._raService.getRentalAgreementDetails(this.uid()!, this.raVersion())).pipe(
        map((ra) => {
          this.item = ra ?? null;
          this.initialize();
        })
      );
    })
  );

  vm$ = combineLatest([
    this._currencyService.getAllCurrencies(),
    this._selectedRentalAgreement$,
    this._vehicleCategoryService.getAllVehicleCategories()
  ]).pipe(map(([currencies, , vehicleCategories]) => ({ currencies, vehicleCategories })));

  constructor() {
    this.form = this._formBuilder.group({
      general: this._formBuilder.group({
        reservationNumber: this._formBuilder.control('', [Validators.required]),
        status: this._formBuilder.control(0, [Validators.required]),
        dateOut: this._formBuilder.control<Date | null>(null, [Validators.required]),
        dateIn: this._formBuilder.control<Date | null>(null, [Validators.required]),
        pickupLocationUid: this._formBuilder.control<{ uid: string; selectedLabel: string } | null>(null, [
          Validators.required
        ]),
        dropOffLocationUid: this._formBuilder.control<{ uid: string; selectedLabel: string } | null>(null, [
          Validators.required
        ]),
        vehicleCategoryUid: this._formBuilder.control('', [Validators.required]),
        servedVehicleCategoryUid: this._formBuilder.control('', [Validators.required]),
        brokerClientId: this._formBuilder.control<{ code: number; selectedLabel: string } | null>(null, [
          Validators.required
        ]),
        brokerReference: this._formBuilder.control('', [Validators.required, Validators.maxLength(30)]),
        rate: this._formBuilder.control('', [Validators.required, Validators.maxLength(30)]),
        ratePeriodId: this._formBuilder.control<number | null>(null, [Validators.required]),
        observations: this._formBuilder.control(''),
        numberOfDays: this._formBuilder.control<number | null>(null, [Validators.required])
      }),
      payment: this._formBuilder.group({
        numberOfDays: this._formBuilder.control<number | null>(null, [Validators.required]),
        currency: this._formBuilder.control('', [Validators.required, Validators.maxLength(3)]),
        taxRate: this._formBuilder.control<number | null>(null, [Validators.required]),
        totalTaxValue: this._formBuilder.control<number | null>(null, [Validators.required]),
        cdwExcess: this._formBuilder.control(0, [Validators.required]),
        tpExcess: this._formBuilder.control(0, [Validators.required]),
        areKmsIncluded: this._formBuilder.control(false, [Validators.required]),
        areKmsFreePerDay: this._formBuilder.control(false, [Validators.required]),
        freeKmsPerDay: this._formBuilder.control(0, [Validators.required]),
        additionalKmsPrice: this._formBuilder.control(0, [Validators.required]),
        discountAmount: this._formBuilder.control(0, [Validators.required])
      }),
      numberOfDays: this._formBuilder.control<number | null>(null, [Validators.required])
    });

    this.form.controls.general.controls.numberOfDays = this.form.controls.numberOfDays;
    this.form.controls.payment.controls.numberOfDays = this.form.controls.numberOfDays;

    this.form.valueChanges.subscribe(() => {
      this.hasUnsavedData = this.form.dirty;
    });

    effect(() => {
      if (this.mode() === 'create') {
        this.form.disable();
        this.form.controls.general.controls.reservationNumber.enable();
        this.form.controls.general.controls.reservationNumber.markAsDirty();
        this.form.controls.general.controls.reservationNumber.markAsTouched();
        return;
      }
      if (this.mode() === 'readonly') {
        this.form.disable();
        return;
      }
      this.enableUpdatingForm();
    });
  }

  @HostListener('window:beforeunload', ['$event'])
  unloadNotification($event: BeforeUnloadEvent) {
    if (this.hasUnsavedData) {
      $event.preventDefault();
      $event.returnValue = ''; // Required for Chrome
    }
  }

  ngOnInit() {
    const config = this.config();

    if (config.mode === 'create' && config.reservationNumber) {
      this.form.controls.general.controls.reservationNumber.setValue(config.reservationNumber);
      this.loadDraftFromReservation();
      return;
    }
  }

  unlockEditing() {
    if (this.mode() === 'readonly' && this.raVersion() === 'latest') {
      this.config.set({ mode: 'update', uid: this.uid() });
    }
  }

  lockEditing() {
    if (this.mode() === 'update') {
      this.config.set({ mode: 'readonly', uid: this.uid(), version: this.raVersion() });
      // Re-fetch re-seeds the drafts from the server, discarding the staged edits.
      this.refreshItem();
    }
  }

  // The details response carries the drivers and value items, so the whole rental agreement loads
  // in a single request: both go into their signal stores.
  private seedChildDraftsFromItem() {
    if (!this.item) return;

    this.setDriversArray(this.item.drivers ?? []);
    this.setValuesArray(this.item.valueItems ?? []);
    this._raDriversLoaded = true;
    this._raValuesLoaded = true;
    this._raDriversModified = false;
    this._raValuesModified = false;

    // The grids may already be rendered; re-list them from the fresh state after change detection.
    setTimeout(() => {
      this.driversComponent()?.refresh();
      this.valuesComponent()?.refresh();
    });
  }

  private setDriversArray(drivers: RentalAgreementDriverDetailsModel[]) {
    this.drivers.set([...drivers]);
  }

  private setValuesArray(values: RentalAgreementValueDetailsModel[]) {
    this.valueItems.set([...values]);
  }

  onDriversChanged() {
    if (this.mode() !== 'update') return;
    this._raDriversModified = true;
    this.form.markAsDirty();
    this.hasUnsavedData = true;
  }

  onValuesChanged() {
    if (this.mode() !== 'update') return;
    this._raValuesModified = true;
    this.form.markAsDirty();
    this.hasUnsavedData = true;
  }

  enableUpdatingForm() {
    this.form.disable();
    this.form.controls.general.controls.observations.enable();
    this.form.controls.general.controls.dateIn.enable();
    this.form.controls.general.controls.status.enable();
    this.form.controls.general.controls.servedVehicleCategoryUid.enable();
  }

  enableCreatingForm() {
    this.form.enable();
    this.form.controls.general.controls.brokerClientId.disable();
    this.form.controls.general.controls.brokerReference.disable();
    this.form.controls.general.controls.rate.disable();
    this.form.controls.general.controls.ratePeriodId.disable();
    this.form.controls.payment.controls.cdwExcess.disable();
    this.form.controls.payment.controls.tpExcess.disable();
  }

  submit() {
    this.errors = null;
    if (this.form.invalid) return;

    const config = this.config();
    if (config.mode === 'update') {
      const data: RentalAgreementUpdateModel = {
        uid: this.item!.uid,
        ...this.createModelValues()
      };

      // Send a staged collection only when it has been seeded from the server AND the user
      // actually changed it; an omitted collection is left untouched on the server.
      if (this._raDriversLoaded && this._raDriversModified) {
        data.drivers = this.drivers().map((d) => this.raDriverFormToPayload(d));
      }
      if (this._raValuesLoaded && this._raValuesModified) {
        data.valueItems = this.valueItems().map((v) => this.raValueFormToPayload(v));
      }

      this.update(data);
    }
    if (config.mode === 'create') {
      this.create(this.createCompleteModel());
    }
  }

  // Draft-created rows get a random non-guid uid; the backend treats an empty guid as "new".
  // The drivers store already holds details (ISO strings, bare country uid); the payload is the
  // same shape, with non-guid (temp) uids collapsed to the empty guid = "new".
  private raDriverFormToPayload(d: RentalAgreementDriverDetailsModel): RentalAgreementDriverUpdatePayload {
    return { ...d, uid: toBackendUid(d.uid) };
  }

  private raValueFormToPayload(v: RentalAgreementValueDetailsModel): RentalAgreementValueDetailsModel {
    return { ...v, uid: toBackendUid(v.uid) };
  }

  loadDraftFromReservation() {
    const reservationNumber = this.form.controls.general.controls.reservationNumber.value;
    if (!reservationNumber) return;
    this.form.disable();
    this.form.reset();
    this.drivers.set([]);
    this.valueItems.set([]);

    // The reservation details carry the drivers and extras, so one request is enough.
    this._reservationService.getReservationDetailsByNumber(reservationNumber).subscribe({
      next: (reservation) => {
        const drivers = reservation.drivers;
        const values = reservation.valueItems;
        if (
          reservation.status === ReservationStatus.Cancelled ||
          reservation.status === ReservationStatus.NotConfirmed
        ) {
          this.item = null;
          this._toastsService.showError(
            `Reservation status is : ${ReservationStatusesDisplayNames[reservation.status]}.`
          );
          this.form.controls.general.controls.reservationNumber.enable();
          return;
        }

        if (reservation.rentalAgreementUid) {
          this.item = null;
          this._toastsService.showError('A rental agreement has already been created for this reservation.');
          this.form.controls.general.controls.reservationNumber.enable();
          return;
        }
        const ra = {
          ...reservation,
          uid: EMPTY_GUID,
          version: 1,
          closeDate: null,
          totalInvoiceTaxValue: 0,
          totalCashierTaxValue: 0,
          reservationNumber: reservation.number,
          reservationUid: reservation.uid,
          status: RentalAgreementStatus.RaOpen,
          createdDate: new Date().toISOString(),
          modifiedDate: new Date().toISOString(),
          // The create flow stages its children into the drafts below, in the RA-specific shape.
          drivers: [],
          valueItems: []
        } as RentalAgreementDetailsModel;

        this.item = ra;
        this.initialize();
        this.enableCreatingForm();
        this.form.markAsDirty();
        this.form.markAllAsTouched();
        this.setDriversArray(drivers);
        this.setValuesArray(
          values
            .filter((x) => x.isAccepted === true)
            .map((x) => ({ ...x, quantity: x.isByRental ? 1 : ra.numberOfDays }))
        );
        this.driversComponent()?.refresh();
        this.valuesComponent()?.refresh();
      },
      error: () => {
        this.form.controls.general.controls.reservationNumber.enable();
        this._toastsService.showError('Failed to load reservation details.');
      }
    });
  }

  dismiss() {
    if (this.hasUnsavedData) {
      const confirmClose = confirm('You have unsaved changes. Are you sure you want to close?');
      if (!confirmClose) return;
    }
    this._modal.dismiss();
  }

  closeRa() {
    if (!this.item) {
      return;
    }

    const createdModal = this._modalService.open(RentalAgreementCloseDialogComponent, {
      centered: true,
      size: 'lg',
      fullscreen: 'lg',
      scrollable: true
    });

    createdModal.componentInstance.uid.set(this.item.uid);
    createdModal.closed.pipe(switchMap(() => this._selectedRentalAgreement$)).subscribe();
  }

  refreshItem() {
    from([true])
      .pipe(switchMap(() => this._selectedRentalAgreement$))
      .subscribe();
  }

  openMainDriverDialog() {
    const readonly = this.mode() === 'readonly';
    const existing = this.drivers().find((d) => !d.isAdditionalDriver);

    const options: NgbModalOptions = { size: 'lg', centered: true, scrollable: true, fullscreen: 'md' };
    const modalRef = this._modalService.open(RentalAgreementDriversUpsertDialogComponent, options);
    modalRef.componentInstance.config.set(
      existing
        ? readonly
          ? { mode: 'readonly', driver: existing, rentalAgreementVersion: this.raVersion() }
          : { mode: 'update', driver: existing, onlyAdditionalDriver: false }
        : { mode: 'create', rentalAgreementUid: this.uid(), onlyAdditionalDriver: false }
    );
    modalRef.closed.subscribe((value: RentalAgreementDriverDetailsModel) => {
      this.drivers.update((list) =>
        existing
          ? list.map((d) => (d.uid === existing.uid ? { ...value, uid: existing.uid } : d))
          : [...list, { ...value, uid: newClientUid() }]
      );
      this.onDriversChanged();
      this.driversComponent()?.refresh();
    });
  }

  navigateToReservation(reservationUid: string) {
    this._modal.dismiss();
    this._router.navigate(['/operations/reservations'], { queryParams: { selected: reservationUid } });
  }

  generalTabInvalid() {
    return (
      this.form.enabled &&
      ((this.form.controls.general.invalid && this.form.controls.general.touched) ||
        this.form.controls.numberOfDays.invalid)
    );
  }

  generalTabModified() {
    return (
      this.mode() === 'update' &&
      this.form.enabled &&
      this.form.controls.general.valid &&
      (this.form.controls.general.dirty || this.form.controls.numberOfDays.dirty)
    );
  }

  paymentTabInvalid() {
    return (
      this.item &&
      this.form.enabled &&
      ((this.form.controls.payment.invalid && this.form.controls.payment.touched) ||
        this.form.controls.numberOfDays.invalid)
    );
  }

  paymentTabModified() {
    return (
      this.mode() === 'update' &&
      this.form.enabled &&
      this.form.controls.payment.valid &&
      (this.form.controls.payment.dirty || this.form.controls.numberOfDays.dirty)
    );
  }

  getValueGridMinHeight() {
    return window.innerWidth < 576 ? 40 : 32;
  }

  getStatusesOptions(): { label: string; value: RentalAgreementStatus }[] {
    const icons = [
      'fa-regular fa-file-lines text-success',
      'fas fa-circle-check text-success',
      'fa-solid fa-file-invoice-dollar text-secondary',
      'fas fa-money-bill-transfer text-secondary',
      'fas fa-money-bill-transfer text-secondary'
    ];

    return Object.values(RentalAgreementStatus)
      .filter((x) => !isNaN(Number(x)))
      .map((status) => ({
        label: RentalAgreementStatusesDisplayNames[status as RentalAgreementStatus],
        value: status as RentalAgreementStatus,
        icon: icons[status as RentalAgreementStatus]
      }));
  }

  private initialize() {
    if (!this.item) return;
    this.form.reset();
    this.form.setValue({
      general: {
        reservationNumber: this.item.reservationNumber,
        status: this.item.status,
        dateOut: new Date(this.item.dateOut),
        dateIn: new Date(this.item.dateIn),
        pickupLocationUid: null,
        dropOffLocationUid: null,
        vehicleCategoryUid: this.item.vehicleCategoryUid,
        brokerClientId: null,
        servedVehicleCategoryUid: this.item.servedVehicleCategoryUid,
        brokerReference: this.item.brokerReference,
        rate: this.item.rate,
        ratePeriodId: this.item.ratePeriodId,
        observations: this.item.observations ?? '',
        numberOfDays: this.item.numberOfDays
      },
      payment: {
        numberOfDays: this.item.numberOfDays,
        currency: this.item.currency,
        taxRate: this.item.taxRate,
        totalTaxValue: this.toDecimal(this.item.totalTaxValue),
        cdwExcess: this.toDecimal(this.item.cdwExcess),
        tpExcess: this.toDecimal(this.item.tpExcess),
        areKmsIncluded: this.item.areKmsIncluded,
        areKmsFreePerDay: this.item.areKmsFreePerDay,
        freeKmsPerDay: this.item.freeKmsPerDay,
        additionalKmsPrice: this.toDecimal(this.item.additionalKmsPrice),
        discountAmount: this.toDecimal(this.item.discountAmount)
      },
      numberOfDays: this.item.numberOfDays
    });

    if (this.item.pickupLocationUid) {
      this._locationService.getLocationByCodeOrUid(this.item.pickupLocationUid).subscribe((loc) => {
        this.form.controls.general.controls.pickupLocationUid.setValue({
          uid: loc.uid,
          selectedLabel: `${loc.referenceCode} - ${loc.name}`
        });
      });
    }

    if (this.item.dropOffLocationUid) {
      this._locationService.getLocationByCodeOrUid(this.item.dropOffLocationUid).subscribe((loc) => {
        this.form.controls.general.controls.dropOffLocationUid.setValue({
          uid: loc.uid,
          selectedLabel: `${loc.referenceCode} - ${loc.name}`
        });
      });
    }

    if (this.item.brokerClientId != null) {
      this._brokerService.getBrokerByCode(this.item.brokerClientId).subscribe((broker) => {
        this.form.controls.general.controls.brokerClientId.setValue({
          code: broker.code,
          selectedLabel: `${broker.code} - ${broker.name}`
        });
      });
    }

    if (this.mode() !== 'create') {
      this.seedChildDraftsFromItem();
    }

    this.hasUnsavedData = false;
    this.historyCollapsed = true;
  }

  private update(data: RentalAgreementUpdateModel) {
    this.showDropOffDateCahngedWarning()
      .pipe(
        tap(() => (this.loading = true)),
        switchMap(() => this._raService.updateRentalAgreement(data)),
        switchMap(() => this._selectedRentalAgreement$),
        catchError((error: unknown | HttpErrorResponse) => {
          this.loading = false;
          this.hasUnsavedData = false;
          this.errors = getErrorMessages(error);
          return EMPTY;
        })
      )
      .subscribe(() => {
        this.loading = false;
        this._toastsService.showSuccess('Rental agreement updated successfully.');
        // The pipeline has already re-fetched the details, which re-seeded the child drafts with
        // the server-recalculated state.
        this.config.set({ mode: 'readonly', uid: this.uid(), version: this.raVersion() });
      });
  }

  private create(data: RentalAgreementCreateModel) {
    this.loading = true;
    this._raService.createReservation(data).subscribe({
      next: (response) => {
        this.drivers.set([]);
        this.valueItems.set([]);
        this._toastsService.showSuccess('Rental agreement created successfully.');
        this._router.navigate(['/operations/rental-agreements'], {
          queryParams: { selected: response.uid },
          queryParamsHandling: 'merge'
        });
      },
      error: (error) => {
        this.loading = false;
        this.errors = getErrorMessages(error);
      }
    });
  }

  private createModelValues() {
    const { general, payment } = this.form.getRawValue();
    return {
      ...general,
      ...payment,
      brokerClientId: general.brokerClientId!.code,
      ratePeriodId: general.ratePeriodId!,
      dateOut: this.formatDate(general.dateOut!),
      dateIn: this.formatDate(general.dateIn!),
      numberOfDays: payment.numberOfDays!,
      taxRate: payment.taxRate!,
      totalTaxValue: payment.totalTaxValue!,
      pickupLocationUid: general.pickupLocationUid!.uid,
      dropOffLocationUid: general.dropOffLocationUid!.uid
    };
  }

  private createCompleteModel(): RentalAgreementCreateModel {
    return {
      ...this.createModelValues(),
      drivers: this.drivers().map((d) => ({
        ...this.raDriverFormToPayload(d),
        rentalAgreementUid: EMPTY_GUID
      })),
      valueItems: this.valueItems().map((v) => ({
        ...this.raValueFormToPayload(v),
        rentalAgreementUid: EMPTY_GUID
      }))
    };
  }

  private showDropOffDateCahngedWarning() {
    if (
      this.item &&
      this.form.controls.general.controls.dateIn.dirty &&
      this.formatDate(this.form.controls.general.controls.dateIn.value!) !== this.item.dateIn
    ) {
      const modalRef = this._modalService.open(ConfirmDialogComponent, { centered: true });
      modalRef.componentInstance.title = 'Drop-off date changed';
      modalRef.componentInstance.message =
        'Changing the drop-off date will not automatically update the quantity for "DAYVALUE" items. Please verify and update them manually if needed.';
      modalRef.componentInstance.confirmBtnText = 'Ok';
      modalRef.componentInstance.style = 'success';
      modalRef.componentInstance.icon = 'fa fa-warning text-warning';

      return modalRef.closed;
    }

    return of(true);
  }

  private formatDate(dateTime: Date): string {
    return new Date(
      Date.UTC(
        dateTime.getFullYear(),
        dateTime.getMonth(),
        dateTime.getDate(),
        dateTime.getHours(),
        dateTime.getMinutes(),
        dateTime.getSeconds(),
        dateTime.getMilliseconds()
      )
    )
      .toISOString()
      .substring(0, 19);
  }

  private toDecimal(value: number): number {
    return Math.round((value + Number.EPSILON) * 100) / 100;
  }
}
